import {
  MOCK_AI_STYLES,
  MOCK_THEMES,
  MOCK_VIDEO_MOTIONS,
  captionStyleById,
  localizedName,
  localizedDescription,
  localizedStory,
  resolveKioskHierarchy,
  resolveMotionsForStyle,
  resolveCaptionStyleId,
  resolveSampleVideoUrl,
  type AIStyle,
  type CaptionStyle,
  type OutputPlan,
  type Theme,
  type VideoMotion,
} from "@fiket/booth-schema";
import { useMemo } from "react";
import type { Language } from "../store/router";
import { useBoothConfig } from "../store/booth";
import { useRouter } from "../store/router";

/** config 가 비었을 때의 폴백 — MOCK 테마·스타일을 서버(boothPublic)와 같은 규칙으로 정규화 */
const MOCK_HIERARCHY = resolveKioskHierarchy([...MOCK_THEMES], [...MOCK_AI_STYLES]);

/**
 * 키오스크가 쓸 스타일 목록(전 테마). `/api/booth/:slug/config` 가 부스 스코프의 활성 스타일을
 * 테마와 함께 내려주므로 별도 호출 없이 config 를 그대로 쓴다. 비었으면 MOCK 폴백.
 * 서버가 themeId 를 미분류로 정규화해 주므로 여기선 항상 채워져 있다고 본다.
 */
export function useStyles(): readonly AIStyle[] {
  const styles = useBoothConfig((s) => s.config?.styles ?? null);
  return styles && styles.length > 0 ? styles : MOCK_HIERARCHY.styles;
}

/**
 * 키오스크에 카드로 노출할 테마 — 활성 스타일이 하나 이상 있는 테마만 (빈 테마는 막다른 길).
 * config 의 themes 가 비어 있으면(구버전 API 등) 스타일의 themeId 로부터 유도하지 않고 MOCK 폴백.
 */
export function useThemes(): readonly Theme[] {
  const themes = useBoothConfig((s) => s.config?.themes ?? null);
  const styles = useStyles();
  return useMemo(() => {
    const source = themes && themes.length > 0 ? themes : MOCK_HIERARCHY.themes;
    const withStyles = new Set(styles.map((s) => s.themeId));
    return source.filter((t) => withStyles.has(t.id));
  }, [themes, styles]);
}

/** 선택된 테마 하위 스타일만. 테마가 없으면(직접 진입 등) 전체. */
export function useStylesForTheme(themeId: string | null): readonly AIStyle[] {
  const styles = useStyles();
  return useMemo(
    () => (themeId ? styles.filter((s) => s.themeId === themeId) : styles),
    [styles, themeId]
  );
}

export function useSelectedTheme(): Theme | null {
  const themes = useThemes();
  const id = useRouter((s) => s.selectedThemeId);
  return themes.find((t) => t.id === id) ?? null;
}

export function useSelectedStyle(): AIStyle | null {
  const styles = useStyles();
  const id = useRouter((s) => s.selectedStyleId);
  return styles.find((s) => s.id === id) ?? null;
}

export function styleName(style: AIStyle | null | undefined, language: Language): string {
  if (!style) return "";
  return localizedName(style, language);
}

/** 스타일 설명 — 언어별 필드(descriptionEn/Ja/Zh), 비어 있으면 ko 폴백 (schema localizedDescription) */
export function styleDescription(style: AIStyle | null | undefined, language: Language): string {
  if (!style) return "";
  return localizedDescription(style, language);
}

/** 생성 화면 소개 문구 — 해당 언어의 story 만, 없으면 "" (폴백 없음, schema localizedStory) */
export function styleStory(style: AIStyle | null | undefined, language: Language): string {
  if (!style) return "";
  return localizedStory(style, language);
}

/**
 * 선택한 스타일에서 고를 수 있는 영상 동작 (BOOTH-60 1b) — config 의 활성 동작을 `resolveMotionsForStyle` 로 거른다
 * (스타일 `motionIds` 순서, 없으면 활성 전체 sortOrder 순 — BOOTH-86). config 가 없으면(MOCK 폴백) MOCK_VIDEO_MOTIONS. 스타일이 없으면 빈 배열.
 */
export function useMotionsForStyle(style: AIStyle | null | undefined): readonly VideoMotion[] {
  const motions = useBoothConfig((s) => s.config?.motions ?? null);
  return useMemo(() => {
    if (!style) return [];
    const source = motions ?? MOCK_VIDEO_MOTIONS;
    return resolveMotionsForStyle(source, style);
  }, [motions, style]);
}

/** 영상 소개 화면(9.1) 샘플 — 선택 테마 샘플 → 부스 기본 샘플 → null(화면 건너뜀). BOOTH-60 1b-2 */
export function useSampleVideoUrl(): string | null {
  const theme = useSelectedTheme();
  const booth = useBoothConfig((s) => s.config?.booth ?? null);
  return resolveSampleVideoUrl(theme, booth);
}

/**
 * 현재 선택된 테마 → 부스 → 기본(`cinema`) 순으로 자막 스타일을 해석한다 (BOOTH-85).
 * `resolveCaptionStyleId(theme, booth)` → `captionStyleById(id)`.
 */
export function useCaptionStyle(): CaptionStyle {
  const theme = useSelectedTheme();
  const booth = useBoothConfig((s) => s.config?.booth ?? null);
  return captionStyleById(resolveCaptionStyleId(theme, booth));
}

export function motionName(motion: VideoMotion, language: Language): string {
  return localizedName(motion, language);
}

export function themeName(theme: Theme | null | undefined, language: Language): string {
  if (!theme) return "";
  return localizedName(theme, language);
}

/** 테마 카드 킥커 — 새 필드 없이 카루셀 순번·code 에서 파생: "05 · JEJU · FANTASY" (HERIT 01) */
export function themeKicker(theme: Theme, index: number): string {
  return `${String(index + 1).padStart(2, "0")} · ${theme.code.toUpperCase().replaceAll("_", " · ")}`;
}

/** 테마 대표 이미지 — 지정 썸네일이 없으면 첫 하위 스타일 썸네일로 폴백. 둘 다 없으면 빈 문자열. */
export function themeThumbnail(theme: Theme, styles: readonly AIStyle[]): string {
  if (theme.thumbnailUrl) return theme.thumbnailUrl;
  return styles.find((s) => s.themeId === theme.id && s.thumbnailUrl)?.thumbnailUrl ?? "";
}

/** 활성 인쇄 프리셋의 출력 계획 (BOOTH-35). 구버전 API 등으로 없으면 null. */
export function useOutputPlan(): OutputPlan | null {
  return useBoothConfig((s) => s.config?.outputPlan ?? null);
}

const FALLBACK_ASPECT = { w: 3, h: 4 };

/**
 * 변환 결과·카메라 뷰포트·인화지 프레임이 공유하는 출력 비율.
 * 인쇄 프리셋의 목표 픽셀(targetPx) → 없으면 gemini 설정 outputAspect → 기본 3:4.
 * 픽셀 쌍을 그대로 비율로 쓴다 (`--ar` 은 w/h 만 본다).
 */
export function useOutputAspect(): { w: number; h: number } {
  const targetPx = useBoothConfig((s) => s.config?.outputPlan?.targetPx ?? null);
  const aspect = useBoothConfig((s) => s.config?.geminiConfig.outputAspect ?? null);
  return targetPx ?? aspect ?? FALLBACK_ASPECT;
}
